import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    IconButton,
    Stack,
    Chip,
    Autocomplete,
    Paper
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
    Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
    DiaryCategory,
    DiaryItemDto,
    DiaryItemType,
    DiaryVisibility,
    ReminderChannel,
    DiaryItemUpsertDto,
    DiaryCreatedSource,
    DiaryItemStatus,
    DiaryLinkDto,
    DiaryReminderDto,
    DiaryShareDto,
    LinkedEntityType
} from "../../models/diaryTypes";

import {
    RecurrenceFormState,
    defaultRecurrenceFromParent,
    parseRecurrenceRule,
    DOW,
    buildRecurrenceRule,
} from "./recurrenceRule";

import { useApiClient } from '../../components/useApiClient';
import { useUser } from '../../components/UserContext';
import useAuthToken from '../../functions/useAuthToken';
import { ClientUser } from "../../models/UserProfile";

import ReminderOffsetSelector from "./ReminderOffsetSelector";
import { getReminderKey } from './reminder-validation';
import LinksEditor from './LinksEditorComponent';
import DiaryChildrenPanel from './DiaryChildrenPanel';
import DiaryChildModal from './DiaryChildModal';

import type {  DiaryPaymentScheduleDto, PaymentMilestoneDisplay } from "../../models/Payments";

import { EntryAction } from './DiaryChildrenPanel';

import { DateTimeRangeInput, DueDateInput } from '../../components/DateInput/DatePresets';

export type DiaryModalMode = "parent" | "child";
export type ViewMode = "add" | "edit";

function isoToLocalInput(iso?: string | null): string {
    if (!iso) return "";

    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
        return "";
    }

    // datetime-local expects YYYY-MM-DDTHH:mm
    const pad = (n: number) => String(n).padStart(2, "0");

    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}


function localInputToIso(local?: string | null): string | null {
    if (!local?.trim()) return null;

    const date = new Date(local);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toISOString();
}

function formatOffset(offsetMinutes: number) {
    if (offsetMinutes === 0) return "At time";
    const abs = Math.abs(offsetMinutes);
    const sign = offsetMinutes < 0 ? "Before" : "After";

    if (abs % 1440 === 0) return `${abs / 1440} day(s) ${sign}`;
    if (abs % 60 === 0) return `${abs / 60} hour(s) ${sign}`;
    return `${abs} min ${sign}`;
}

function formatUserLabel(u: ClientUser) {
    const name = (u.displayName ?? "").trim();
    return name ? `${name} (${u.email})` : u.email;
}

type Props = {
    open: boolean;
    viewMode: ViewMode;
    item: DiaryItemDto;
    clientUsers: ClientUser[];
    onClose: () => void;
    onSave: (dto: DiaryItemUpsertDto, id?: number) => Promise<void> | void;
    refreshParent: (id: number) => Promise<DiaryItemDto>;
    regenerateSeries: (id: number, fromUtc?: string, toUtc?: string) => Promise<any>;
    updateDiaryItem: (id: number, dto: DiaryItemUpsertDto) => void;
    createChildDiaryItem: (parentId: number, dto: DiaryItemUpsertDto) => Promise<any>;
};
export default function DiaryItemModal({ open, viewMode, item, clientUsers, onClose, onSave, refreshParent, regenerateSeries, updateDiaryItem, createChildDiaryItem }: Props) {

    type TargetUserOption =
        | { kind: "creator"; label: string }
        | { kind: "user"; user: ClientUser; label: string };

        //Handle client roles and Api
    const { activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired;

    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    const targetUserOptions: TargetUserOption[] = useMemo(() => {
        const creator: TargetUserOption = { kind: "creator", label: "Creator (default)" };
        const users: TargetUserOption[] = clientUsers.map(u => ({
            kind: "user",
            user: u,
            label: formatUserLabel(u),
        }));
        return [creator, ...users];
    }, [clientUsers]);

    const isEdit = viewMode === 'edit';
    const isSystemGenerated = item.createdSource === DiaryCreatedSource.System;

    // Base fields
    const [itemState, setItemState] = useState<DiaryItemDto>(item);
    const [title, setTitle] = useState(item.title ?? "");
    const [description, setDescription] = useState(item.description ?? "");
    const [type, setType] = useState<DiaryItemType>(item.type);
    const [category, setCategory] = useState<DiaryCategory>(item.category);
    const [visibility, setVisibility] = useState<DiaryVisibility>(item.visibility);
    const [allDay, setAllDay] = useState<boolean>(!!item.allDay);
    const [timezoneId, setTimezoneId] = useState<string>(item.timezoneId ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    const viewOnly = item.status === DiaryItemStatus.Completed || item.status === DiaryItemStatus.Cancelled || isViewOnly;

    const dialogHeading = category === DiaryCategory.Payment ? "Payment" : isEdit ? (viewOnly ? "View Diary Item" : "Edit Diary Item") : "Create Diary Item";
    //Payment Details
    const [paymentSchedule, setPaymentSchedule] = useState<DiaryPaymentScheduleDto | null>(null);

    const milestoneByDiaryItemId = useMemo(() => {
        const record: Record<number, PaymentMilestoneDisplay> = {};
        paymentSchedule?.milestones?.forEach(m => {
            record[m.diaryItemId] = m;
        });
        return record;
    }, [paymentSchedule]);

    async function getPaymentSummaryByDiaryItem(diaryItemId: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getPaymentDetailsByDiaryItem');

            return await get<DiaryPaymentScheduleDto | null>(`payment-schedules/by-parent-diary-item/${diaryItemId}`,accessToken);
        } catch (e: any) {
            console.error("Get Payment Summary Details Error", e);
            return null;
        }

    }

    async function completeDiaryItem(id: number) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in completeDiaryItem');

            const formData = new FormData();
            const res = await post(`diary/${id}/complete`, accessToken, formData);
        }
        catch (e) {
            console.error('Error in completeDiaryItem', e);
            throw new Error('Error in completeDiaryItem');
        }
    }

    function formatMoney(v: number) {
        return Number(v || 0).toFixed(2);
    }

    //Linked Entities
    const [linksState, setLinksState] = useState<DiaryLinkDto[]>(item.links ?? []);

    // Times
    // Times
    const [dateRange, setDateRange] = useState<{
        startDate?: string;
        endDate?: string;
    }>({
        startDate: isoToLocalInput(item.startUtcStr),
        endDate: isoToLocalInput(item.endUtcStr),
    });

    const [dueLocal, setDueLocal] = useState<string>(
        isoToLocalInput(item.dueUtcStr)
    );

    // Reminders and shares
    const [reminders, setReminders] = useState<DiaryReminderDto[]>(
        (item.reminders ?? []).map(r => ({
            offsetMinutes: r.offsetMinutes,
            channel: r.channel,
            targetUserId: r.targetUserId ?? null,
            isEnabled: r.isEnabled ?? true,
        }))
    );

    // Recurrence
    const [recurrence, setRecurrence] = useState<RecurrenceFormState>(() => {
        // first render
        const parsed = parseRecurrenceRule(item.recurrenceRule);
        return parsed ?? defaultRecurrenceFromParent(item);
    });


    useEffect(() => {
        if (!open) return;

        const parsed = parseRecurrenceRule(itemState.recurrenceRule);
        if (parsed) {
            setRecurrence(parsed);
        } else {
            setRecurrence(defaultRecurrenceFromParent(item));
        }
    }, [open, itemState.recurrenceRule]);



    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

    const [shares, setShares] = useState<DiaryShareDto[]>(
        (item.shares ?? []).map(s => ({
            userId: s.userId,
            accessLevel: s.accessLevel,
        }))
    );

    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    //Child Modal
    const [childDialogOpen, setChildDialogOpen] = useState(false);
    const [editingChild, setEditingChild] = useState<DiaryItemDto | null>(null); // null means "create"
    const [isChildSaving, setIsChildSaving] = useState(false);

    function openCreateChild() {
        setEditingChild(null);
        setChildDialogOpen(true);
    }

    function openEditChild(child: DiaryItemDto) {
        setEditingChild(child);
        setChildDialogOpen(true);
    }

    function closeChildDialog() {
        if (isChildSaving) return;
        setChildDialogOpen(false);
        setEditingChild(null);
    }

    // If user toggles type, ensure the required date/time is present.
    // Event End remains optional and must NOT be defaulted.
    useEffect(() => {
        setValidationError(null);

        if (type === DiaryItemType.Event) {
            setDateRange(prev => {
                if (prev.startDate) {
                    return prev;
                }

                return {
                    ...prev,
                    startDate: isoToLocalInput(new Date().toISOString()),
                };
            });
        } else {
            if (!dueLocal) {
                setDueLocal(
                    isoToLocalInput(new Date().toISOString())
                );
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    useEffect(() => {
        if (!open) return;

        setItemState(item);

        // Refresh date fields from the current diary item whenever
        // the modal is opened or a different item is supplied.
        setDateRange({
            startDate: isoToLocalInput(item.startUtcStr),
            endDate: isoToLocalInput(item.endUtcStr),
        });

        setDueLocal(
            isoToLocalInput(item.dueUtcStr)
        );

    }, [open, item]);

    useEffect(() => {
        let alive = true;

        async function load() {
            setPaymentSchedule(null);
            if (!open || !item?.id) return;

            const res = await getPaymentSummaryByDiaryItem(item.id);

            if (!alive) return;
            setPaymentSchedule(res);
        }

        load();
        return () => { alive = false; };
    }, [open, item?.id]);


    function addReminder() {
        const newReminder: DiaryReminderDto = {
            offsetMinutes: -1440,
            channel: ReminderChannel.InApp,
            targetUserId: null,
            isEnabled: true,
        };

        const isDuplicate = reminders.some(
            reminder =>
                getReminderKey(reminder) === getReminderKey(newReminder)
        );

        if (isDuplicate) {
            setShowDuplicateWarning(true);
            return;
        }

        setReminders(prev => [
            ...prev,
            newReminder
        ]);
    }

    function updateReminder(
        i: number,
        patch: Partial<DiaryReminderDto>
    ) {
        const currentReminder = reminders[i];

        if (!currentReminder) return;

        // Build the reminder as it would look after applying the change
        const updatedReminder: DiaryReminderDto = {
            ...currentReminder,
            ...patch,
        };

        // Check all OTHER reminders for the same key.
        // Do not compare the reminder against itself.
        const isDuplicate = reminders.some((reminder, idx) =>
            idx !== i &&
            getReminderKey(reminder) === getReminderKey(updatedReminder)
        );

        if (isDuplicate) {
            setShowDuplicateWarning(true);
            return;
        }

        setReminders(prev =>
            prev.map((reminder, idx) =>
                idx === i
                    ? updatedReminder
                    : reminder
            )
        );
    }

    function removeReminder(i: number) {
        setReminders(prev => prev.filter((_, idx) => idx !== i));
    }

    function addShare() {
        setShares(prev => [...prev, { userId: 0, accessLevel: 1 }]);
    }

    function updateShare(i: number, patch: Partial<DiaryShareDto>) {
        setShares(prev => {
            const next = prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s));

            // remove duplicates by userId (keep first)
            const seen = new Set<number>();
            return next.filter(s => {
                if (!s.userId) return true;
                if (seen.has(s.userId)) return false;
                seen.add(s.userId);
                return true;
            });
        });
    }

    function removeShare(i: number) {
        setShares(prev => prev.filter((_, idx) => idx !== i));
    }

    async function handleRegenerate() {
        if (recurrence) {
            await regenerateSeries(itemState.id); // now->+365 default in backend or pass dates
            const refreshed = await refreshParent(itemState.id);
            setItemState(refreshed);
        }
    }

    function validate(): string | null {
        if (!title.trim()) {
            return "Title is required.";
        }

        // Artwork/Portfolio Links
        for (const l of linksState) {
            if (l.entityType === LinkedEntityType.Artwork) {
                const n = Number(l.entityId);

                if (!Number.isInteger(n) || n <= 0) {
                    return "Artwork links must have a valid numeric Artwork ID.";
                }
            }
        }

        if (type === DiaryItemType.Event) {
            const startLocal = dateRange.startDate;
            const endLocal = dateRange.endDate;

            console.log(
                "Validating Event:",
                "startLocal=",
                startLocal,
                "endLocal=",
                endLocal
            );

            // Start is mandatory for an Event
            if (!startLocal) {
                return "Start date/time is required for events.";
            }

            const startIso = localInputToIso(startLocal);

            if (!startIso) {
                return "Start date/time is invalid.";
            }

            // End is optional
            if (endLocal) {
                const endIso = localInputToIso(endLocal);

                if (!endIso) {
                    return "End date/time is invalid.";
                }

                const startTime = new Date(startIso).getTime();
                const endTime = new Date(endIso).getTime();

                if (endTime < startTime) {
                    return "End date/time must be after start date/time.";
                }
            }
        } else {
            // Due date is mandatory for Tasks
            if (!dueLocal) {
                return "Due date/time is required for tasks.";
            }

            const dueIso = localInputToIso(dueLocal);

            if (!dueIso) {
                return "Due date/time is invalid.";
            }
        }

        // Shares: userId must be > 0
        for (const s of shares) {
            if (!s.userId || s.userId <= 0) {
                return "Shares must have a valid User.";
            }
        }

        return null;
    }

    async function handleSave() {
        const err = validate();

        console.log("Validation result:", err);

        setValidationError(err);

        if (err) {
            return;
        }

        // Convert local form values to UTC ISO strings for the API.
        //
        // Empty optional values are converted to null.
        const dateStart = localInputToIso(dateRange.startDate);
        const dateEnd = localInputToIso(dateRange.endDate);
        const dateDue = localInputToIso(dueLocal);

        const recurrenceRule = buildRecurrenceRule(recurrence);

        const dto: DiaryItemUpsertDto = {
            title: title.trim(),

            description: description?.trim()
                ? description.trim()
                : null,

            type,
            category,
            visibility,
            allDay,

            timezoneId: timezoneId?.trim()
                ? timezoneId.trim()
                : null,

            // Event dates
            startUtc:
                type === DiaryItemType.Event
                    ? dateStart
                    : null,

            endUtc:
                type === DiaryItemType.Event
                    ? dateEnd
                    : null,

            // Task date
            dueUtc:
                type === DiaryItemType.Task
                    ? dateDue
                    : null,

            reminders: reminders.map(r => ({
                offsetMinutes: Number(r.offsetMinutes),
                channel: r.channel,
                targetUserId: r.targetUserId ?? null,
                isEnabled: !!r.isEnabled,
            })),

            shares: shares.map(s => ({
                userId: Number(s.userId),
                accessLevel: s.accessLevel,
            })),

            links: linksState,

            recurrenceRule,
        };

        setSaving(true);

        try {
            console.log(
                "Saving DiaryItem with DTO:",
                dto
            );

            await onSave(
                dto,
                isEdit ? item.id : undefined
            );

            handleRegenerate();
        } finally {
            setSaving(false);
        }
    }

    async function onComplete(childId: number) {
        await completeDiaryItem(childId);
        await refreshParent(itemState.id);
    }

    const handleEntryAction = (action: EntryAction, data: DiaryItemDto | number) => {
        switch (action) {
            case 'edit':
                openEditChild(data as DiaryItemDto);
                break;

            case 'view':
                openEditChild(data as DiaryItemDto);
                break;

            case 'complete':
                const entryId = data as number;
                onComplete(entryId);
                break;
        }
    };

    return (
        <Dialog open={open} keepMounted
            onClose={(_, reason) => {
            if (childDialogOpen) return;
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            onClose();
            }}
            fullWidth maxWidth="md">
            <DialogTitle>
                {dialogHeading}
            </DialogTitle>

            <DialogContent>
                {validationError && (
                    <Box sx={{ mb: 2 }}>
                        <Typography color="error">{validationError}</Typography>
                    </Box>
                )}

                <Stack spacing={2}>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        disabled={viewOnly}
                    />

                    <TextField
                        label="Description / notes"
                        value={description ?? ""}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        disabled={viewOnly}
                    />
                    <Box sx={{ mt: 1 }}>
                        <LinksEditor
                            links={linksState}
                            setLinks={setLinksState}
                            
                        />
                    </Box>                   

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={type}
                                onChange={(e) => setType(e.target.value as DiaryItemType)}
                                disabled={viewOnly || isSystemGenerated}
                            >
                                <MenuItem value={DiaryItemType.Event}>Event</MenuItem>
                                <MenuItem value={DiaryItemType.Task}>Task</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                label="Category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value as DiaryCategory)}
                                disabled={viewOnly || isSystemGenerated}
                            >
                                <MenuItem value={DiaryCategory.General}>General</MenuItem>
                                <MenuItem value={DiaryCategory.Insurance}>Insurance</MenuItem>
                                <MenuItem value={DiaryCategory.Payment}>Payment</MenuItem>
                                <MenuItem value={DiaryCategory.Valuation}>Valuation</MenuItem>
                                <MenuItem value={DiaryCategory.Shipping}>Shipping</MenuItem>
                                <MenuItem value={DiaryCategory.Exhibition}>Exhibition</MenuItem>
                                <MenuItem value={DiaryCategory.Conservation}>Conservation</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Visibility</InputLabel>
                            <Select
                                label="Visibility"
                                value={visibility}
                                onChange={(e) => setVisibility(e.target.value as DiaryVisibility)}
                                disabled={viewOnly}
                            >
                                <MenuItem value={DiaryVisibility.Private}>Private (shared users only)</MenuItem>
                                <MenuItem value={DiaryVisibility.ClientWide}>Client-wide</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    {paymentSchedule && (
                        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Payment summary
                                    </Typography>

                                    {paymentSchedule.nextDueUtc && new Date(paymentSchedule.nextDueUtc) < new Date() && (
                                        <Chip size="small" color="error" variant="outlined" label="Overdue" />
                                    )}
                                </Stack>

                                <Typography variant="body2" color="text.secondary">
                                    {paymentSchedule.title}
                                    {paymentSchedule.nextDueUtc
                                        ? ` • Next due: ${new Date(paymentSchedule.nextDueUtc).toLocaleDateString()}`
                                        : ""}
                                </Typography>

                                <Divider sx={{ my: 1 }} />

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <Typography variant="body2">
                                        <strong>Total:</strong> {paymentSchedule.totalAmount.toFixed(2)} {paymentSchedule.currencyCode}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Paid:</strong> {paymentSchedule.totalPaid.toFixed(2)} {paymentSchedule.currencyCode}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Outstanding:</strong> {paymentSchedule.totalOutstanding.toFixed(2)} {paymentSchedule.currencyCode}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    )}


                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                        <FormControlLabel
                            control={<Switch checked={allDay} onChange={(e) => setAllDay(e.target.checked)} disabled={viewOnly} />}
                            label="All day"
                        />
                        {/*<TextField*/}
                        {/*    label="Timezone"*/}
                        {/*    value={timezoneId}*/}
                        {/*    onChange={(e) => setTimezoneId(e.target.value)}*/}
                        {/*    fullWidth*/}
                        {/*    helperText="Stored for display; times are saved as UTC."*/}
                        {/*/>*/}
                    </Stack>

                    <Divider />

                    {/* Timing */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Schedule
                    </Typography>

                    {type === DiaryItemType.Event ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <DateTimeRangeInput
                                value={dateRange}
                                onChange={setDateRange}
                                startLabel="Start"
                                endLabel="End"
                                validateRange={true}
                            />
                        </Stack>
                    ) : (
                            <DueDateInput
                                value={dueLocal}
                                onChange={setDueLocal}
                                showTime={true}
                                label="Task Deadline"
                            />
                    )}
                    <Divider />

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                                <Typography sx={{ fontWeight: 600 }}>Recurrence</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={recurrence.enabled}
                                            onChange={(e) => setRecurrence(r => ({ ...r, enabled: e.target.checked }))}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            disabled={viewOnly || isSystemGenerated}
                                        />
                                    }
                                    label={recurrence.enabled ? "On" : "Off"}
                                />
                            </Stack>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Stack spacing={2}>
                                <FormControl fullWidth disabled={!recurrence.enabled}>
                                    <InputLabel>Frequency</InputLabel>
                                    <Select
                                        label="Frequency"
                                        value={recurrence.frequency}
                                        onChange={(e) => setRecurrence(r => ({ ...r, frequency: e.target.value as any }))}
                                        disabled={viewOnly || isSystemGenerated}
                                    >
                                        <MenuItem value="DAILY">Daily</MenuItem>
                                        <MenuItem value="WEEKLY">Weekly</MenuItem>
                                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                                        <MenuItem value="YEARLY">Yearly</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    disabled={!recurrence.enabled || viewOnly || isSystemGenerated}
                                    label="Every"
                                    type="number"
                                    value={recurrence.interval}
                                    onChange={(e) => setRecurrence(r => ({ ...r, interval: Math.max(1, Number(e.target.value || 1)) }))}
                                    inputProps={{ min: 1 }}
                                    helperText={
                                        recurrence.frequency === "DAILY" ? "Days" :
                                            recurrence.frequency === "WEEKLY" ? "Weeks" :
                                                recurrence.frequency === "MONTHLY" ? "Months" : "Years"
                                    }
                                />

                                {recurrence.enabled && recurrence.frequency === "WEEKLY" && (
                                    <Stack spacing={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Days</Typography>
                                        <ToggleButtonGroup
                                            value={recurrence.byDays}
                                            onChange={(_, val) => setRecurrence(r => ({ ...r, byDays: val }))}
                                            disabled={viewOnly || isSystemGenerated}
                                        >
                                            {DOW.map(d => (
                                                <ToggleButton key={d.token} value={d.token} size="small">
                                                    {d.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </Stack>
                                )}

                                {recurrence.enabled && recurrence.frequency === "MONTHLY" && (
                                    <TextField
                                        label="Day of month"
                                        type="number"
                                        value={recurrence.byMonthDay ?? 1}
                                        onChange={(e) => setRecurrence(r => ({ ...r, byMonthDay: Math.min(31, Math.max(1, Number(e.target.value || 1))) }))}
                                        inputProps={{ min: 1, max: 31 }}
                                        disabled={viewOnly || isSystemGenerated}
                                    />
                                )}

                                <TextField
                                    disabled={!recurrence.enabled || viewOnly || isSystemGenerated}
                                    label="Ends on (optional)"
                                    type="date"
                                    value={recurrence.until ?? ""}
                                    onChange={(e) => setRecurrence(r => ({ ...r, until: e.target.value || null }))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Leave blank for no end date."
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    <Divider />

                    {/* Scheduled occurrences (generated) */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Scheduled occurrences
                        </Typography>

                        {/*<Button startIcon={<AutorenewIcon />} variant="outlined" onClick={handleRegenerate} disabled={viewOnly}>*/}
                        {/*    Regenerate*/}
                        {/*</Button>*/}
                    </Stack>

                    <Divider />

                    {/* Reminders */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Reminders
                        </Typography>
                        <Button startIcon={<AddIcon />} onClick={addReminder} variant="outlined" disabled={viewOnly}>
                            Add reminder
                        </Button>
                    </Stack>

                    {reminders.length === 0 ? (
                        <Typography color="text.secondary">No reminders configured.</Typography>
                    ) : (
                        <Stack spacing={1}>
                            {reminders.map((r, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 1.5,
                                    }}
                                >
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                        <ReminderOffsetSelector
                                            value={r.offsetMinutes}
                                            onChange={(newOffset: number) =>
                                                updateReminder(idx, { offsetMinutes: newOffset })
                                            }
                                            label="Reminder time"
                                            viewOnly={viewOnly}
                                        />

                                        <FormControl sx={{ width: { xs: "100%", sm: 450 } }}>
                                            <InputLabel>Channel</InputLabel>
                                            <Select
                                                label="Channel"
                                                value={r.channel}
                                                onChange={(e) => updateReminder(idx, { channel: e.target.value as ReminderChannel })}
                                                disabled={viewOnly}
                                            >
                                                <MenuItem value={ReminderChannel.InApp}>In-app</MenuItem>
                                                <MenuItem value={ReminderChannel.Email}>Email</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <Autocomplete
                                            disabled={viewOnly}
                                            options={targetUserOptions}
                                            value={
                                                r.targetUserId == null
                                                    ? targetUserOptions[0]
                                                    : targetUserOptions.find(o => o.kind === "user" && o.user.userId === r.targetUserId) ?? targetUserOptions[0]
                                            }
                                            onChange={(_, opt) => {
                                                if (!opt || opt.kind === "creator") updateReminder(idx, { targetUserId: null });
                                                else updateReminder(idx, { targetUserId: opt.user.userId });
                                            }}
                                            getOptionLabel={(opt) => opt.label}
                                            isOptionEqualToValue={(opt, val) => opt.label === val.label}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Target"
                                                    sx={{ width: { xs: "100%", sm: 320 } }}
                                                />
                                            )}
                                        />


                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!r.isEnabled}
                                                    onChange={(e) => updateReminder(idx, { isEnabled: e.target.checked })}
                                                    disabled={viewOnly}
                                                />
                                            }
                                            label="Enabled"
                                        />

                                        <IconButton onClick={() => removeReminder(idx)} aria-label="Remove reminder" disabled={viewOnly}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>

                                    {/* Duplicate Warning Dialog */}
                                    <Dialog open={showDuplicateWarning} onClose={() => setShowDuplicateWarning(false)}>
                                        <DialogTitle>Duplicate Reminder</DialogTitle>
                                        <DialogContent>
                                            <Typography>
                                                This would create a duplicate reminder!
                                            </Typography>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setShowDuplicateWarning(false)}>Cancel</Button>
                                        </DialogActions>
                                    </Dialog>
                                </Box>
                            ))}
                        </Stack>
                    )}

                    <Divider />

                    {/* Shares */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Shares
                        </Typography>
                        {visibility === DiaryVisibility.Private &&
                            <Button startIcon={<AddIcon />} onClick={addShare} variant="outlined" disabled={viewOnly || isSystemGenerated}>
                                Add share
                            </Button>
                        }
                    </Stack>

                    <Divider />

                    <DiaryChildrenPanel
                        children={itemState.children ?? []}
                        onAddLinkedTask={openCreateChild}
                        onEditChild={openEditChild}
                        onAction={handleEntryAction}
                        paymentCurrencyCode={paymentSchedule?.currencyCode}
                        milestoneByDiaryItemId={paymentSchedule ? milestoneByDiaryItemId : undefined}
                        onCompleteChild={async (childId) => {
                            await completeDiaryItem(childId);
                            await refreshParent(itemState.id);
                        }}
                        viewOnly={viewOnly || isSystemGenerated}
                    />

                    <DiaryChildModal
                        open={childDialogOpen}
                        parent={itemState}
                        child={editingChild}
                        onClose={closeChildDialog}
                        onSaved={refreshParent}
                        updateDiaryItem={updateDiaryItem}
                        createChildDiaryItem={createChildDiaryItem}
                    />


                    {visibility === DiaryVisibility.ClientWide ? (
                        <Box>
                            <Chip label="Client-wide visibility: shares are not required" variant="outlined" />
                            {shares.length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    You can still keep shares populated for future changes back to Private, but they do not affect access while Client-wide.
                                </Typography>
                            )}
                        </Box>
                    ) : shares.length === 0 ? (
                        <Typography color="text.secondary">
                            No shares. Private items are visible only to you unless you add users here.
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {shares.map((s, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 1.5,
                                    }}
                                >
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                        <Autocomplete
                                            options={clientUsers}
                                            value={clientUsers.find(u => u.userId === s.userId) ?? null}
                                            onChange={(_, val) => updateShare(idx, { userId: val?.userId ?? 0 })}
                                            getOptionLabel={(opt) => formatUserLabel(opt)}
                                            isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="User"
                                                    placeholder="Select a user"
                                                    sx={{ width: { xs: "100%", sm: 360 } }}
                                                />
                                            )}
                                        />


                                        <FormControl sx={{ width: { xs: "100%", sm: 180 } }}>
                                            <InputLabel>Access</InputLabel>
                                            <Select
                                                label="Access"
                                                value={s.accessLevel}
                                                onChange={(e) => updateShare(idx, { accessLevel: e.target.value as any })}
                                                disabled={viewOnly}
                                            >
                                                <MenuItem value={1}>View</MenuItem>
                                                <MenuItem value={2}>Edit</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <Box sx={{ flex: 1 }} />

                                        <IconButton onClick={() => removeShare(idx)} aria-label="Remove share" disabled={viewOnly}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    {viewOnly ? "Close" : "Cancel"}
                </Button>
                {!viewOnly && 
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}
